import { useCallback } from 'react';
import BaseTimePicker from 'react-time-picker';

import { useRecoilValue } from 'recoil';

import { cn, ia } from 'lib/helpers/utility';
import useTimezone from 'lib/hooks/useTimezone';
import { useWorkingHours } from 'lib/hooks/useWorkingHours';

import { currentPractice } from 'components/practice/practiceState';
import 'components/shared/DateInputPicker/lib/DateInputPicker.scss';

import Header from '../shared/Header';
import ClearIcon from '../shared/ClearIcon';
import Popover from '../Popovers/Popover/Popover';

import Timezone from './components/Timezone';

import 'react-time-picker/dist/TimePicker.css';

/**
 * @param {import('./lib/propTypes').TimePickerProps} props
 */

const TimePicker = (props) => {
  const {
    value,
    onChange = () => {},
    disabled = false,
    className,
    inputClassName,
    panelClassName,
    parentClassName,
    buttonClassName,
    hasOptions = true,
    options = [],
    optionPosition = 'left',
    isClearable = false,
    stepMinutes = 5,
    label = '',
    required = false,
    error = null,
    isTimezone = true,
    showTimezone = true,
    timezone: timezoneProp = null,
    date = null
  } = props;

  const currPractice = useRecoilValue(currentPractice);
  const timezoneFromHook = useTimezone({ enabled: !timezoneProp });
  const userTimezone = timezoneProp || timezoneFromHook;
  const workingHours = useWorkingHours(currPractice, stepMinutes);

  const optionsList = hasOptions
    ? ia(options)
      ? options
      : workingHours.map((item) => ({ ...item, onClick: () => onChange(item.value) }))
    : [];

  const hasValidOptions = hasOptions && ia(optionsList);
  const isValue = Boolean(value);

  const handleClear = useCallback(() => onChange(null), [onChange]);

  return (
    <div className={cn('w-full', parentClassName)} data-field-error={error ? '' : undefined}>
      <Header label={label} required={required} />
      <Popover
        disabled={disabled}
        className={cn(
          'custom-picker relative w-full min-w-[140px] rounded-md border border-solid border-neutral-200 bg-white px-3 !pr-0 focus-within:border-primary-300 focus-within:!outline focus-within:outline-2 focus-within:outline-[#CBF0FD]',
          className
        )}
        innerWrapperClassName="!static"
        panelClassName={cn(
          'max-h-[350px] overflow-y-auto w-full',
          !hasValidOptions && 'hidden',
          panelClassName
        )}
        position={optionPosition}
        text={value}
        customButton={
          <BaseTimePicker
            className={cn(isValue && 'filled', hasValidOptions && 'is-options', inputClassName)}
            disabled={disabled}
            format="hh:mm a"
            hourPlaceholder="HH"
            minutePlaceholder="mm"
            isOpen={false}
            disableClock
            clockIcon={null}
            clearIcon={
              <ClearIcon isClearable={isClearable} onClick={handleClear} isValue={isValue} />
            }
            value={value}
            onChange={onChange}
          />
        }
        customButtonClassName={cn(
          'w-full h-10 flex items-center justify-end pr-3',
          hasValidOptions ? 'cursor-pointer' : 'cursor-default',
          buttonClassName
        )}
        customButtonTrigger={
          showTimezone ? (
            <Timezone value={value} isTimezone={isTimezone} timezone={userTimezone} date={date} />
          ) : null
        }
        options={optionsList}
      />
      {error && <p className="pt-2 text-sm text-danger-500">{error}</p>}
    </div>
  );
};

export default TimePicker;
